import React, { useEffect } from "react";
import { CSSProperties, FC } from "react";
import { XYCoord, useDragLayer } from "react-dnd";
import { Identifier } from "dnd-core";

type ComponentWithItem<T> = React.ReactElement<{ item: T }>;

type PreviewComponent<T> = {
  itemType: string;
  component: ComponentWithItem<T>;
};

type DragLayerProps<T = any> = {
  component: ComponentWithItem<T> | PreviewComponent<T>[];
};

const layerStyles: CSSProperties = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 100,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

const getItemStyles = (
  initialOffset: XYCoord | null,
  currentOffset: XYCoord | null
): CSSProperties => {
  if (!initialOffset || !currentOffset) {
    return {
      display: "none",
    };
  }

  let { x, y } = currentOffset;

  const transform = `translate(${x}px, ${y}px)`;
  return {
    transform,
    WebkitTransform: transform,
  };
};

const isPreviewArray = <T,>(
  preview: ComponentWithItem<T> | PreviewComponent<T>[]
): preview is PreviewComponent<T>[] => {
  return Array.isArray(preview);
};

const DragLayer: FC<DragLayerProps> = (props) => {
  const { component } = props;

  const { itemType, isDragging, item, initialOffset, currentOffset } =
    useDragLayer((monitor) => ({
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
    }));

  const renderItem = <T,>(
    item: T,
    component: ComponentWithItem<T> | PreviewComponent<T>[],
    itemType: Identifier | null
  ): React.ReactElement | null => {
    if (!isPreviewArray(component))
      return React.cloneElement(component, {
        item: item,
      });
    if (typeof itemType !== "string") return null;
    const correspondingComponent = component.find(
      (c) => c.itemType === itemType
    );
    if (typeof correspondingComponent === "undefined") return null;

    return React.cloneElement(correspondingComponent.component, { item });
  };

  if (!isDragging) {
    return null;
  }

  return (
    <div style={layerStyles}>
      <div style={getItemStyles(initialOffset, currentOffset)}>
        {renderItem(item, component, itemType)}
      </div>
    </div>
  );
};

export default DragLayer;
